/**
 * Manufacturing Industry Configuration
 *
 * Supports: CNC Machines, Robots, Conveyors, Presses, Furnaces, Assembly Lines
 */

import { IndustryConfig } from '../industryTypes'

export const manufacturingConfig: IndustryConfig = {
  id: 'manufacturing',
  name: 'Manufacturing',
  description: 'Discrete and process manufacturing equipment monitoring',
  icon: 'Factory',

  terminology: {
    asset: 'Equipment',
    assets: 'Equipment',
    assetPlural: 'Equipment',
    assetType: 'Equipment Type',
    failure: 'Breakdown',
    health: 'Health',
    healthScore: 'Health Score',
    healthy: 'Operational',
    degraded: 'Degraded',
    critical: 'Down',
    alert: 'Alert',
    process: 'Production',
    maintenance: 'Maintenance',
    inspection: 'Inspection',
    downtime: 'Downtime',
    workOrder: 'Work Order',
    prediction: 'Prediction',
    anomaly: 'Anomaly',
    trend: 'Trend',
    risk: 'Risk',
    facility: 'Plant',
    facilityPlural: 'Plants',
    location: 'Line',
    operator: 'Operator',
    supervisor: 'Supervisor',
    calibration: 'Calibration',
    compliance: 'Compliance',
    criticality: 'Criticality',
  },

  assetTypes: [
    {
      id: 'cnc_machine',
      name: 'CNC Machine',
      icon: 'Cog',
      description: 'Computer numerical control machining centers',
      category: 'Machining',
      defaultMetrics: ['spindle_load', 'vibration', 'temperature', 'cycle_time'],
      maintenanceTypes: ['tool_change', 'lubrication', 'alignment', 'calibration'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Line-stopping equipment', downtimeCostMultiplier: 10 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Major production impact', downtimeCostMultiplier: 5 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Moderate impact', downtimeCostMultiplier: 2 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Minimal impact', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'robot_arm',
      name: 'Robot Arm',
      icon: 'Bot',
      description: 'Industrial robotic manipulators',
      category: 'Automation',
      defaultMetrics: ['joint_torque', 'position_accuracy', 'cycle_time', 'motor_temperature'],
      maintenanceTypes: ['joint_lubrication', 'calibration', 'cable_inspection', 'gripper_service'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Cell-stopping robot', downtimeCostMultiplier: 8 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Major cell impact', downtimeCostMultiplier: 4 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Partial redundancy', downtimeCostMultiplier: 2 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup available', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'conveyor',
      name: 'Conveyor System',
      icon: 'ArrowRightLeft',
      description: 'Material handling conveyors',
      category: 'Material Handling',
      defaultMetrics: ['belt_speed', 'motor_current', 'belt_tension', 'alignment'],
      maintenanceTypes: ['belt_inspection', 'roller_replacement', 'motor_service', 'alignment'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Main line conveyor', downtimeCostMultiplier: 10 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Feed conveyor', downtimeCostMultiplier: 5 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary line', downtimeCostMultiplier: 2 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Non-critical path', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'press',
      name: 'Press Machine',
      icon: 'ArrowDownToLine',
      description: 'Hydraulic and mechanical presses',
      category: 'Forming',
      defaultMetrics: ['press_force', 'hydraulic_pressure', 'cycle_time', 'die_temperature'],
      maintenanceTypes: ['die_inspection', 'hydraulic_service', 'seal_replacement', 'alignment'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Bottleneck press', downtimeCostMultiplier: 12 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Primary forming', downtimeCostMultiplier: 6 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary forming', downtimeCostMultiplier: 3 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Redundant capacity', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'furnace',
      name: 'Industrial Furnace',
      icon: 'Flame',
      description: 'Heat treatment and melting furnaces',
      category: 'Thermal',
      defaultMetrics: ['temperature', 'fuel_consumption', 'atmosphere_composition', 'cycle_time'],
      maintenanceTypes: ['refractory_inspection', 'burner_service', 'thermocouple_calibration', 'door_seal'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Continuous process', downtimeCostMultiplier: 15 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Batch critical', downtimeCostMultiplier: 8 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Standard batch', downtimeCostMultiplier: 3 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup unit', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'assembly_station',
      name: 'Assembly Station',
      icon: 'Wrench',
      description: 'Manual and semi-automated assembly',
      category: 'Assembly',
      defaultMetrics: ['cycle_time', 'quality_rate', 'tool_usage', 'ergonomic_score'],
      maintenanceTypes: ['tool_calibration', 'fixture_inspection', 'pneumatic_service', 'workstation_audit'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Pacemaker station', downtimeCostMultiplier: 10 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Constraint station', downtimeCostMultiplier: 5 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Standard station', downtimeCostMultiplier: 2 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Flexible capacity', downtimeCostMultiplier: 1 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'oee',
      name: 'Overall Equipment Effectiveness',
      icon: 'Gauge',
      description: 'Comprehensive equipment performance metric',
      businessValue: 'Maximize production output and minimize waste',
      category: 'universal',
      metrics: [
        { id: 'availability', name: 'Availability', unit: '%', description: 'Uptime vs planned time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 85, critical: 75 } },
        { id: 'performance', name: 'Performance', unit: '%', description: 'Actual vs ideal cycle time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 85, critical: 75 } },
        { id: 'quality', name: 'Quality', unit: '%', description: 'Good parts vs total parts', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99, warning: 95, critical: 90 } },
        { id: 'oee_score', name: 'OEE Score', unit: '%', description: 'Combined effectiveness', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 65, critical: 50 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 65, max: 85 }, critical: { max: 65 } },
      visualizations: ['gauge', 'line_chart', 'comparison', 'trend'],
    },
    {
      id: 'condition_monitoring',
      name: 'Condition Monitoring',
      icon: 'Activity',
      description: 'Real-time equipment condition via sensors',
      businessValue: 'Early detection of degradation and wear',
      category: 'universal',
      metrics: [
        { id: 'vibration', name: 'Vibration', unit: 'mm/s', description: 'Vibration velocity', dataType: 'continuous', aggregation: 'max', goodDirection: 'lower', defaultThresholds: { healthy: 2.8, warning: 7.1, critical: 11.2 } },
        { id: 'temperature', name: 'Temperature', unit: '°C', description: 'Operating temperature', dataType: 'continuous', aggregation: 'max', goodDirection: 'target', defaultThresholds: { healthy: 65, warning: 80, critical: 95 } },
        { id: 'current', name: 'Motor Current', unit: 'A', description: 'Motor draw current', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 100, warning: 120, critical: 140 } },
        { id: 'pressure', name: 'Hydraulic Pressure', unit: 'bar', description: 'System pressure', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 200, warning: 180, critical: 160 } },
      ],
      thresholds: { healthy: { min: 0 }, warning: { min: 0 }, critical: { min: 0 } },
      visualizations: ['line_chart', 'heatmap', 'distribution', 'trend'],
    },
    {
      id: 'quality_control',
      name: 'Quality Control',
      icon: 'CheckCircle',
      description: 'Product quality and defect tracking',
      businessValue: 'Reduce scrap, rework, and customer complaints',
      category: 'industry_specific',
      metrics: [
        { id: 'defect_rate', name: 'Defect Rate', unit: 'ppm', description: 'Defects per million', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 100, warning: 500, critical: 1000 } },
        { id: 'first_pass_yield', name: 'First Pass Yield', unit: '%', description: 'Parts passing first inspection', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 90 } },
        { id: 'scrap_rate', name: 'Scrap Rate', unit: '%', description: 'Scrapped vs produced', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 1, warning: 3, critical: 5 } },
        { id: 'rework_rate', name: 'Rework Rate', unit: '%', description: 'Parts requiring rework', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 5, critical: 10 } },
      ],
      thresholds: { healthy: { min: 95 }, warning: { min: 90, max: 95 }, critical: { max: 90 } },
      visualizations: ['bar_chart', 'trend', 'distribution', 'comparison'],
    },
    {
      id: 'productivity',
      name: 'Productivity Metrics',
      icon: 'TrendingUp',
      description: 'Production output and efficiency',
      businessValue: 'Maximize throughput and minimize costs',
      category: 'universal',
      metrics: [
        { id: 'cycle_time', name: 'Cycle Time', unit: 'sec', description: 'Time per unit', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 30, warning: 40, critical: 50 } },
        { id: 'throughput', name: 'Throughput', unit: 'units/hr', description: 'Units produced per hour', dataType: 'continuous', aggregation: 'sum', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 80, critical: 60 } },
        { id: 'utilization', name: 'Utilization', unit: '%', description: 'Actual vs available time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 70, critical: 55 } },
        { id: 'changeover_time', name: 'Changeover Time', unit: 'min', description: 'Setup time between runs', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 15, warning: 30, critical: 60 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 70, max: 85 }, critical: { max: 70 } },
      visualizations: ['line_chart', 'bar_chart', 'comparison', 'trend'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'preventive',
      name: 'Preventive Maintenance',
      description: 'Scheduled time-based maintenance',
      category: 'preventive',
      defaultInterval: { value: 500, unit: 'hours' },
      laborRequirements: [
        { role: 'Technician', count: 1, hourlyRate: 45 },
      ],
      estimatedDuration: { min: 1, typical: 2, max: 4, unit: 'hours' },
      skillsRequired: ['mechanical', 'electrical'],
      partsCommonly: ['filters', 'lubricants', 'belts'],
    },
    {
      id: 'predictive',
      name: 'Predictive Maintenance',
      description: 'Condition-based maintenance triggered by AI',
      category: 'predictive',
      laborRequirements: [
        { role: 'Technician', count: 1, hourlyRate: 45 },
        { role: 'Engineer', count: 1, hourlyRate: 75 },
      ],
      estimatedDuration: { min: 2, typical: 4, max: 8, unit: 'hours' },
      skillsRequired: ['diagnostics', 'mechanical', 'electrical'],
      partsCommonly: ['bearings', 'seals', 'motors'],
    },
    {
      id: 'corrective',
      name: 'Corrective Maintenance',
      description: 'Repair after failure',
      category: 'corrective',
      laborRequirements: [
        { role: 'Technician', count: 2, hourlyRate: 45 },
        { role: 'Engineer', count: 1, hourlyRate: 75 },
      ],
      estimatedDuration: { min: 4, typical: 8, max: 24, unit: 'hours' },
      skillsRequired: ['troubleshooting', 'mechanical', 'electrical', 'plc'],
      partsCommonly: ['motors', 'drives', 'controllers', 'actuators'],
    },
    {
      id: 'calibration',
      name: 'Calibration',
      description: 'Precision adjustment and verification',
      category: 'condition_based',
      defaultInterval: { value: 3, unit: 'months' },
      laborRequirements: [
        { role: 'Metrology Technician', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 30, typical: 60, max: 120, unit: 'minutes' },
      skillsRequired: ['metrology', 'precision_measurement'],
      partsCommonly: ['calibration_standards', 'probes'],
    },
  ],

  riskFactors: [
    { id: 'downtime_risk', name: 'Downtime Risk', description: 'Probability of unplanned stoppage', category: 'operational', weight: 0.3, calculationMethod: 'exponential', inputs: ['health_score', 'age', 'maintenance_due'] },
    { id: 'quality_risk', name: 'Quality Risk', description: 'Risk of quality issues', category: 'operational', weight: 0.25, calculationMethod: 'linear', inputs: ['defect_trend', 'calibration_status'] },
    { id: 'safety_risk', name: 'Safety Risk', description: 'Risk of safety incidents', category: 'safety', weight: 0.25, calculationMethod: 'threshold', inputs: ['guard_status', 'interlock_status', 'inspection_due'] },
    { id: 'cost_risk', name: 'Cost Risk', description: 'Risk of excessive costs', category: 'financial', weight: 0.2, calculationMethod: 'linear', inputs: ['repair_history', 'parts_availability'] },
  ],

  complianceStandards: [
    {
      id: 'osha_general',
      name: 'OSHA General Industry',
      shortName: 'OSHA',
      description: 'Occupational Safety and Health Administration',
      authority: 'US Department of Labor',
      region: ['US'],
      requirements: [
        { id: 'machine_guarding', name: 'Machine Guarding', description: 'Proper guarding on moving parts', kpiId: 'guard_status', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'lockout_tagout', name: 'Lockout/Tagout', description: 'LOTO procedures compliance', kpiId: 'loto_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
    {
      id: 'iso_9001',
      name: 'ISO 9001 Quality Management',
      shortName: 'ISO 9001',
      description: 'Quality management system standard',
      authority: 'ISO',
      region: ['Global'],
      requirements: [
        { id: 'calibration_current', name: 'Calibration Status', description: 'All equipment calibrated', kpiId: 'calibration_status', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'maintenance_records', name: 'Maintenance Records', description: 'Complete maintenance documentation', kpiId: 'documentation_complete', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'quarterly',
    },
  ],

  visualization: {
    primaryColor: '#3b82f6',
    secondaryColor: '#8b5cf6',
    healthyColor: '#10b981',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Health Summary',
      context: 'asset_health',
      audience: ['executive', 'manager', 'engineer'],
      template: 'Your {assetType} "{assetName}" is currently {healthStatus}. {trendDescription} Over the past {timeRange}, {kpiSummary}. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'timeRange', type: 'time' },
        { name: 'kpiSummary', type: 'metric' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
    {
      id: 'prediction_alert',
      name: 'Prediction Alert',
      context: 'prediction',
      audience: ['manager', 'engineer', 'technician'],
      template: 'AI {prediction} for {assetType} "{assetName}": {predictionDetails}. Confidence: {confidence}%. Based on analysis of {dataPoints} data points. Recommended action: {recommendation}',
      variables: [
        { name: 'prediction', type: 'terminology' },
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'predictionDetails', type: 'metric' },
        { name: 'confidence', type: 'percentage' },
        { name: 'dataPoints', type: 'metric' },
        { name: 'recommendation', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '7d',
    refreshInterval: 30,
    defaultDashboard: 'command-center',
  },

  // =============================================================================
  // PROFESSIONAL ROLES (Manufacturing-Specific)
  // =============================================================================
  professionalRoles: [
    // SME Roles
    {
      id: 'reliability_engineer',
      category: 'sme',
      name: 'Reliability Engineer',
      shortName: 'RE',
      description: 'Analyzes equipment performance data to improve reliability and reduce failures',
      responsibilities: [
        'Root cause analysis of equipment failures',
        'FMEA (Failure Mode and Effects Analysis)',
        'Reliability modeling and prediction',
        'Maintenance strategy optimization',
        'Spare parts inventory analysis',
      ],
      focusAreas: ['MTBF', 'MTTR', 'failure_patterns', 'reliability_metrics', 'risk_assessment'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'risk', 'action_items'],
      },
      decisionAuthority: ['maintenance_strategy', 'spare_parts_stocking', 'equipment_modifications'],
      reportingTo: 'Maintenance Manager',
      icon: 'Target',
    },
    {
      id: 'process_engineer',
      category: 'sme',
      name: 'Process Engineer',
      shortName: 'PE',
      description: 'Optimizes manufacturing processes for quality, efficiency, and cost',
      responsibilities: [
        'Process optimization and improvement',
        'Quality control parameter setting',
        'Cycle time reduction initiatives',
        'Equipment capability analysis',
        'New process implementation',
      ],
      focusAreas: ['oee', 'cycle_time', 'quality_rate', 'process_capability', 'efficiency'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['technical_details', 'action_items', 'business_impact'],
      },
      decisionAuthority: ['process_parameters', 'quality_limits', 'cycle_time_targets'],
      reportingTo: 'Production Manager',
      icon: 'Settings',
    },
    {
      id: 'maintenance_planner',
      category: 'sme',
      name: 'Maintenance Planner',
      shortName: 'MP',
      description: 'Plans and schedules maintenance activities to minimize production impact',
      responsibilities: [
        'Maintenance scheduling and coordination',
        'Work order management',
        'Resource allocation',
        'Parts and material procurement',
        'Contractor coordination',
      ],
      focusAreas: ['maintenance_backlog', 'schedule_compliance', 'resource_utilization', 'cost_tracking'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['action_items', 'business_impact', 'risk'],
      },
      decisionAuthority: ['maintenance_scheduling', 'parts_ordering', 'contractor_selection'],
      reportingTo: 'Maintenance Manager',
      icon: 'Calendar',
    },
    // Technician Roles
    {
      id: 'maintenance_technician',
      category: 'technician',
      name: 'Maintenance Technician',
      shortName: 'MT',
      description: 'Performs hands-on equipment repairs, troubleshooting, and preventive maintenance',
      responsibilities: [
        'Equipment troubleshooting and repair',
        'Preventive maintenance execution',
        'Parts replacement and installation',
        'Equipment inspections',
        'Work order completion and documentation',
      ],
      focusAreas: ['fault_codes', 'repair_procedures', 'parts_needed', 'safety_requirements', 'tool_requirements'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: false,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items'],
      },
      decisionAuthority: ['repair_methods', 'parts_selection', 'immediate_safety_stops'],
      reportingTo: 'Maintenance Supervisor',
      icon: 'Wrench',
    },
    {
      id: 'instrumentation_technician',
      category: 'technician',
      name: 'Instrumentation Technician',
      shortName: 'IT',
      description: 'Specializes in sensors, PLCs, and control system maintenance',
      responsibilities: [
        'Sensor calibration and replacement',
        'PLC troubleshooting',
        'Control system diagnostics',
        'Instrumentation loop testing',
        'HMI maintenance',
      ],
      focusAreas: ['sensor_readings', 'control_parameters', 'calibration_status', 'signal_integrity'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items'],
      },
      decisionAuthority: ['sensor_selection', 'calibration_methods', 'control_parameter_adjustments'],
      reportingTo: 'Controls Engineer',
      icon: 'Gauge',
    },
    {
      id: 'electrician',
      category: 'technician',
      name: 'Industrial Electrician',
      shortName: 'IE',
      description: 'Handles electrical systems, motors, drives, and power distribution',
      responsibilities: [
        'Motor and drive repairs',
        'Electrical panel maintenance',
        'Power quality troubleshooting',
        'Cable and wiring repairs',
        'Electrical safety inspections',
      ],
      focusAreas: ['motor_current', 'voltage_levels', 'insulation_resistance', 'power_factor', 'electrical_faults'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items', 'risk'],
      },
      decisionAuthority: ['electrical_repairs', 'motor_sizing', 'safety_lockouts'],
      reportingTo: 'Maintenance Supervisor',
      icon: 'Zap',
    },
    // Executive Roles
    {
      id: 'plant_manager',
      category: 'executive',
      name: 'Plant Manager',
      shortName: 'PM',
      description: 'Oversees entire plant operations, production targets, and strategic initiatives',
      responsibilities: [
        'Overall plant performance',
        'Budget and cost management',
        'Strategic planning',
        'Regulatory compliance',
        'Staff development',
      ],
      focusAreas: ['oee', 'cost_per_unit', 'safety_incidents', 'customer_satisfaction', 'on_time_delivery'],
      explanationStyle: {
        depth: 'high_level',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'risk', 'compliance'],
      },
      decisionAuthority: ['capital_expenditure', 'staffing_levels', 'strategic_initiatives'],
      reportingTo: 'VP Operations',
      icon: 'Building',
    },
    {
      id: 'maintenance_manager',
      category: 'executive',
      name: 'Maintenance Manager',
      shortName: 'MM',
      description: 'Manages maintenance department, budgets, and equipment reliability programs',
      responsibilities: [
        'Maintenance budget management',
        'Equipment reliability improvement',
        'Team leadership and development',
        'Contractor management',
        'CMMS oversight',
      ],
      focusAreas: ['maintenance_costs', 'equipment_availability', 'mtbf', 'backlog_hours', 'team_utilization'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['business_impact', 'action_items', 'risk'],
      },
      decisionAuthority: ['maintenance_budget', 'equipment_replacement', 'staff_scheduling'],
      reportingTo: 'Plant Manager',
      icon: 'Users',
    },
    // Analyst Roles
    {
      id: 'data_analyst',
      category: 'analyst',
      name: 'Manufacturing Data Analyst',
      shortName: 'DA',
      description: 'Analyzes production and equipment data to identify trends and opportunities',
      responsibilities: [
        'Data collection and validation',
        'Performance reporting',
        'Trend analysis',
        'Dashboard development',
        'KPI tracking',
      ],
      focusAreas: ['all_metrics', 'trends', 'correlations', 'benchmarks', 'anomalies'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['technical_details', 'business_impact'],
      },
      decisionAuthority: ['report_formats', 'data_collection_methods', 'analysis_priorities'],
      reportingTo: 'Operations Manager',
      icon: 'BarChart',
    },
  ],

  // =============================================================================
  // PARTS CATALOG (Manufacturing Equipment)
  // =============================================================================
  partsCatalog: {
    categories: [
      {
        id: 'bearings',
        name: 'Bearings',
        description: 'Roller, ball, and sleeve bearings for rotating equipment',
        parts: [
          {
            id: 'bearing_6205',
            partNumber: 'SKF-6205-2RS',
            name: 'Deep Groove Ball Bearing 6205',
            description: '25mm bore, sealed, for general purpose applications',
            category: 'bearings',
            manufacturer: 'SKF',
            unitCost: 18.50,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['cnc_machine', 'conveyor', 'press'],
            failureModes: ['bearing_wear', 'vibration_high'],
            averageLifespan: { value: 8760, unit: 'hours' },
            specifications: { bore: '25mm', od: '52mm', width: '15mm' },
          },
          {
            id: 'bearing_6310',
            partNumber: 'SKF-6310-2Z',
            name: 'Deep Groove Ball Bearing 6310',
            description: '50mm bore, shielded, for heavy-duty applications',
            category: 'bearings',
            manufacturer: 'SKF',
            unitCost: 42.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['cnc_machine', 'press', 'furnace'],
            failureModes: ['bearing_wear', 'vibration_high', 'overheating'],
            averageLifespan: { value: 10000, unit: 'hours' },
            specifications: { bore: '50mm', od: '110mm', width: '27mm' },
          },
          {
            id: 'bearing_taper',
            partNumber: 'TIMKEN-32008X',
            name: 'Tapered Roller Bearing 32008X',
            description: '40mm bore, for high axial load applications',
            category: 'bearings',
            manufacturer: 'Timken',
            unitCost: 65.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['press', 'cnc_machine'],
            failureModes: ['bearing_wear', 'misalignment', 'overloading'],
            averageLifespan: { value: 12000, unit: 'hours' },
            specifications: { bore: '40mm', od: '68mm', width: '19mm' },
          },
        ],
      },
      {
        id: 'belts_drives',
        name: 'Belts & Drives',
        description: 'V-belts, timing belts, and drive components',
        parts: [
          {
            id: 'vbelt_b68',
            partNumber: 'GATES-B68',
            name: 'V-Belt B68',
            description: 'Classical V-belt, 68 inch effective length',
            category: 'belts_drives',
            manufacturer: 'Gates',
            unitCost: 12.50,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['conveyor', 'cnc_machine', 'press'],
            failureModes: ['belt_wear', 'belt_slip', 'belt_break'],
            averageLifespan: { value: 4380, unit: 'hours' },
          },
          {
            id: 'timing_belt_5m',
            partNumber: 'GATES-5M-450',
            name: 'Timing Belt 5M-450',
            description: 'HTD timing belt, 5mm pitch, 450mm length',
            category: 'belts_drives',
            manufacturer: 'Gates',
            unitCost: 28.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['robot_arm', 'cnc_machine', 'assembly_station'],
            failureModes: ['belt_wear', 'tooth_shear', 'timing_drift'],
            averageLifespan: { value: 6000, unit: 'hours' },
          },
        ],
      },
      {
        id: 'motors',
        name: 'Motors & Drives',
        description: 'Electric motors, servo motors, and variable frequency drives',
        parts: [
          {
            id: 'motor_5hp',
            partNumber: 'ABB-M3AA-5HP',
            name: '5HP 3-Phase AC Motor',
            description: '5HP, 1800 RPM, TEFC, 184T frame',
            category: 'motors',
            manufacturer: 'ABB',
            unitCost: 850.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['conveyor', 'press', 'cnc_machine'],
            failureModes: ['motor_burnout', 'bearing_failure', 'winding_fault'],
            averageLifespan: { value: 40000, unit: 'hours' },
            specifications: { hp: 5, rpm: 1800, voltage: '460V', frame: '184T' },
          },
          {
            id: 'vfd_10hp',
            partNumber: 'ABB-ACS355-10HP',
            name: 'Variable Frequency Drive 10HP',
            description: 'VFD for 10HP motor, 460V input',
            category: 'motors',
            manufacturer: 'ABB',
            unitCost: 1250.00,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['conveyor', 'press', 'cnc_machine', 'furnace'],
            failureModes: ['drive_fault', 'overheating', 'capacitor_failure'],
            averageLifespan: { value: 50000, unit: 'hours' },
            specifications: { hp: 10, inputVoltage: '460V', outputFreq: '0-400Hz' },
          },
          {
            id: 'servo_motor_2kw',
            partNumber: 'FANUC-AM20-2000',
            name: 'Servo Motor 2kW',
            description: 'AC servo motor with encoder, for CNC and robotics',
            category: 'motors',
            manufacturer: 'Fanuc',
            unitCost: 2800.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['cnc_machine', 'robot_arm'],
            failureModes: ['encoder_failure', 'motor_burnout', 'brake_failure'],
            averageLifespan: { value: 30000, unit: 'hours' },
            specifications: { power: '2kW', torque: '6.37Nm', rpm: 3000 },
          },
        ],
      },
      {
        id: 'hydraulics',
        name: 'Hydraulic Components',
        description: 'Pumps, valves, cylinders, and seals',
        parts: [
          {
            id: 'hydraulic_pump',
            partNumber: 'VICKERS-PVB29',
            name: 'Hydraulic Piston Pump PVB29',
            description: '29cc/rev displacement, pressure compensated',
            category: 'hydraulics',
            manufacturer: 'Eaton Vickers',
            unitCost: 2200.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['press'],
            failureModes: ['pump_wear', 'seal_leak', 'cavitation'],
            averageLifespan: { value: 20000, unit: 'hours' },
            specifications: { displacement: '29cc/rev', maxPressure: '280bar' },
          },
          {
            id: 'hydraulic_cylinder_seal_kit',
            partNumber: 'PARKER-SK-4X8',
            name: 'Cylinder Seal Kit 4x8',
            description: 'Complete seal kit for 4" bore, 8" stroke cylinder',
            category: 'hydraulics',
            manufacturer: 'Parker',
            unitCost: 185.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['press'],
            failureModes: ['seal_leak', 'cylinder_drift'],
            averageLifespan: { value: 5000, unit: 'hours' },
          },
          {
            id: 'solenoid_valve',
            partNumber: 'REXROTH-4WE6',
            name: 'Directional Control Valve 4WE6',
            description: '4-way directional valve, solenoid operated',
            category: 'hydraulics',
            manufacturer: 'Bosch Rexroth',
            unitCost: 450.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['press', 'robot_arm'],
            failureModes: ['solenoid_failure', 'valve_stuck', 'seal_leak'],
            averageLifespan: { value: 2000000, unit: 'cycles' },
          },
        ],
      },
      {
        id: 'sensors',
        name: 'Sensors & Instrumentation',
        description: 'Temperature, pressure, vibration, and position sensors',
        parts: [
          {
            id: 'vibration_sensor',
            partNumber: 'SKF-CMSS2200',
            name: 'Vibration Sensor CMSS2200',
            description: 'Industrial accelerometer, 100mV/g sensitivity',
            category: 'sensors',
            manufacturer: 'SKF',
            unitCost: 380.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['cnc_machine', 'conveyor', 'press', 'furnace'],
            failureModes: ['sensor_drift', 'cable_damage'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { sensitivity: '100mV/g', freqRange: '0.5-10kHz' },
          },
          {
            id: 'temp_sensor_rtd',
            partNumber: 'OMEGA-PR-11-2-100',
            name: 'RTD Temperature Sensor PT100',
            description: 'PT100 RTD probe, 1/2" NPT, -50 to 400°C',
            category: 'sensors',
            manufacturer: 'Omega',
            unitCost: 95.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['furnace', 'cnc_machine', 'press'],
            failureModes: ['sensor_drift', 'open_circuit', 'sheath_damage'],
            averageLifespan: { value: 3, unit: 'years' },
            specifications: { type: 'PT100', range: '-50 to 400°C', accuracy: '±0.1°C' },
          },
          {
            id: 'prox_sensor',
            partNumber: 'IFM-IG5845',
            name: 'Inductive Proximity Sensor',
            description: 'M18 inductive sensor, 8mm sensing distance, PNP NO',
            category: 'sensors',
            manufacturer: 'IFM',
            unitCost: 65.00,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['cnc_machine', 'conveyor', 'robot_arm', 'assembly_station'],
            failureModes: ['sensor_failure', 'false_trigger', 'cable_damage'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { type: 'Inductive', range: '8mm', output: 'PNP NO' },
          },
        ],
      },
      {
        id: 'consumables',
        name: 'Consumables & Lubricants',
        description: 'Oils, greases, filters, and other consumables',
        parts: [
          {
            id: 'hydraulic_oil_68',
            partNumber: 'MOBIL-DTE25',
            name: 'Hydraulic Oil ISO 46',
            description: 'Premium hydraulic oil, 5 gallon pail',
            category: 'consumables',
            manufacturer: 'Mobil',
            unitCost: 85.00,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['press', 'cnc_machine'],
            failureModes: ['lubrication_failure'],
            specifications: { viscosity: 'ISO 46', volume: '5 gal' },
          },
          {
            id: 'bearing_grease',
            partNumber: 'SKF-LGMT2',
            name: 'Multi-Purpose Grease LGMT2',
            description: 'Industrial bearing grease, 1kg can',
            category: 'consumables',
            manufacturer: 'SKF',
            unitCost: 28.00,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['cnc_machine', 'conveyor', 'press', 'robot_arm'],
            failureModes: ['lubrication_failure', 'bearing_wear'],
            specifications: { type: 'Lithium complex', weight: '1kg' },
          },
          {
            id: 'air_filter',
            partNumber: 'DONALDSON-P181011',
            name: 'Panel Air Filter',
            description: 'Intake air filter for electrical cabinets',
            category: 'consumables',
            manufacturer: 'Donaldson',
            unitCost: 12.00,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['cnc_machine', 'robot_arm', 'press'],
            failureModes: ['overheating'],
            averageLifespan: { value: 3, unit: 'months' },
          },
        ],
      },
    ],
  },

  // =============================================================================
  // ROOT CAUSE INTELLIGENCE (Manufacturing)
  // =============================================================================
  rootCauseLibrary: {
    categories: [
      {
        id: 'mechanical',
        name: 'Mechanical',
        description: 'Root causes related to mechanical components and systems',
        icon: 'Cog',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'bearing_wear',
            name: 'Bearing Wear',
            description: 'Progressive deterioration of bearing surfaces due to friction, contamination, or improper lubrication',
            category: 'mechanical',
            symptoms: [
              'Increased vibration at bearing frequencies',
              'Elevated operating temperature',
              'Audible noise (grinding, squealing)',
              'Shaft runout',
              'Metal particles in lubricant',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure vibration spectrum using handheld analyzer',
                expectedResult: 'Elevated peaks at BPFO, BPFI, or BSF frequencies',
                tools: ['Vibration analyzer', 'Accelerometer'],
                duration: 15,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Check bearing temperature with IR thermometer',
                expectedResult: 'Temperature >20°C above ambient indicates problem',
                tools: ['IR thermometer'],
                duration: 5,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Inspect lubricant sample for metal particles',
                expectedResult: 'Metal flakes or discoloration indicates wear',
                tools: ['Sample container', 'Patch test kit'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 4,
                action: 'Measure shaft runout with dial indicator',
                expectedResult: 'Runout >0.002" suggests bearing damage',
                tools: ['Dial indicator', 'Magnetic base'],
                duration: 20,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Implement vibration-based condition monitoring',
              'Follow proper lubrication schedule',
              'Use correct lubricant type and quantity',
              'Ensure proper shaft alignment',
              'Control operating temperature',
            ],
            typicalProbability: 0.25,
            affectedComponents: ['bearings', 'shafts', 'housings', 'seals'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 2, typical: 4, max: 8, unit: 'hours' },
            costRange: { min: 200, max: 2500, currency: 'USD' },
            industryReferences: ['ISO 10816-3', 'SKF Bearing Failure Analysis'],
          },
          {
            id: 'misalignment',
            name: 'Shaft Misalignment',
            description: 'Angular or parallel offset between coupled shafts causing excessive stress and vibration',
            category: 'mechanical',
            symptoms: [
              'High vibration at 1x and 2x running speed',
              'Coupling wear patterns',
              'Premature seal failures',
              'Elevated bearing temperatures',
              'Loose foundation bolts',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform laser alignment measurement',
                expectedResult: 'Angular offset >0.001"/inch or parallel >0.002" indicates misalignment',
                tools: ['Laser alignment system'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Inspect coupling for wear patterns',
                expectedResult: 'Uneven wear indicates misalignment',
                tools: ['Flashlight', 'Inspection mirror'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Check foundation and mounting bolts',
                expectedResult: 'All bolts properly torqued, no soft foot',
                tools: ['Torque wrench', 'Feeler gauges'],
                duration: 20,
                skillLevel: 'basic',
              },
            ],
            preventiveActions: [
              'Perform precision laser alignment after installation',
              'Establish alignment baselines',
              'Correct soft foot conditions',
              'Monitor thermal growth',
              'Use flexible couplings appropriately rated',
            ],
            typicalProbability: 0.20,
            affectedComponents: ['couplings', 'bearings', 'seals', 'shafts'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 1, typical: 3, max: 6, unit: 'hours' },
            costRange: { min: 150, max: 1200, currency: 'USD' },
            industryReferences: ['ANSI/ASA S2.75', 'ISO 10816'],
          },
          {
            id: 'belt_tension',
            name: 'Belt Tension Issues',
            description: 'Incorrect belt tension causing slippage, premature wear, or excessive bearing loads',
            category: 'mechanical',
            symptoms: [
              'Belt squealing noise',
              'Belt slip (speed reduction)',
              'Premature belt wear or cracking',
              'Hot bearings on driven equipment',
              'Belt dust accumulation',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure belt tension with tension gauge',
                expectedResult: 'Tension within manufacturer spec (typically 3-5 lb deflection per inch of span)',
                tools: ['Belt tension gauge'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Inspect belt condition',
                expectedResult: 'No cracks, glazing, or excessive wear',
                tools: ['Flashlight'],
                duration: 5,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Check sheave alignment',
                expectedResult: 'Sheaves aligned within 1/16" per foot of center distance',
                tools: ['Straight edge', 'String line'],
                duration: 15,
                skillLevel: 'basic',
              },
            ],
            preventiveActions: [
              'Check tension during PM intervals',
              'Replace belts in matched sets',
              'Ensure proper sheave alignment',
              'Use correct belt type for application',
              'Monitor drive temperature',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['belts', 'sheaves', 'bearings'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 30, typical: 60, max: 120, unit: 'minutes' },
            costRange: { min: 50, max: 400, currency: 'USD' },
          },
        ],
      },
      {
        id: 'electrical',
        name: 'Electrical',
        description: 'Root causes related to electrical systems and components',
        icon: 'Zap',
        color: '#f59e0b',
        rootCauses: [
          {
            id: 'motor_winding_fault',
            name: 'Motor Winding Fault',
            description: 'Degradation or failure of motor windings due to insulation breakdown, overheating, or contamination',
            category: 'electrical',
            symptoms: [
              'High motor current draw',
              'Phase imbalance',
              'Motor overheating',
              'Reduced motor torque',
              'Burning smell',
              'Tripping overloads',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure phase-to-phase resistance',
                expectedResult: 'All phases within 5% of each other',
                tools: ['Digital multimeter'],
                duration: 10,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Perform insulation resistance test (megger)',
                expectedResult: 'IR > 1 MΩ per kV of rating + 1 MΩ minimum',
                tools: ['Megohmmeter'],
                duration: 15,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Check motor current with clamp meter',
                expectedResult: 'Current balanced and within nameplate FLA',
                tools: ['Clamp meter'],
                duration: 5,
                skillLevel: 'basic',
              },
              {
                order: 4,
                action: 'Perform surge test if available',
                expectedResult: 'No turn-to-turn shorts indicated',
                tools: ['Surge tester'],
                duration: 30,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Regular insulation resistance testing',
              'Maintain proper motor cooling',
              'Protect from moisture and contamination',
              'Avoid frequent starts',
              'Size motor appropriately for load',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['motor_windings', 'motor_insulation', 'motor'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 4, typical: 8, max: 24, unit: 'hours' },
            costRange: { min: 500, max: 5000, currency: 'USD' },
            industryReferences: ['IEEE 43', 'NEMA MG-1'],
          },
          {
            id: 'vfd_fault',
            name: 'VFD/Drive Fault',
            description: 'Variable frequency drive malfunction due to component failure, overheating, or electrical issues',
            category: 'electrical',
            symptoms: [
              'Drive fault codes displayed',
              'Motor not running or erratic speed',
              'Overheating drive cabinet',
              'DC bus voltage abnormal',
              'Power module failure indication',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Record fault codes from drive display',
                expectedResult: 'Identify specific fault type (overcurrent, overvoltage, etc.)',
                tools: ['None'],
                duration: 5,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Check DC bus voltage',
                expectedResult: 'DC bus voltage = √2 × AC input (±10%)',
                tools: ['Digital multimeter'],
                duration: 10,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Inspect cooling fans and heat sinks',
                expectedResult: 'Fans operating, heat sinks clear of debris',
                tools: ['Flashlight'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 4,
                action: 'Check power module IGBTs',
                expectedResult: 'Forward drop consistent across all phases',
                tools: ['Digital multimeter'],
                duration: 20,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Regular cabinet cleaning and filter replacement',
              'Monitor drive temperatures',
              'Keep firmware updated',
              'Ensure proper grounding',
              'Avoid excessive starts/stops',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['vfd', 'power_module', 'control_board', 'capacitors'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 1, typical: 4, max: 16, unit: 'hours' },
            costRange: { min: 200, max: 3500, currency: 'USD' },
            industryReferences: ['Drive manufacturer manual'],
          },
          {
            id: 'power_quality',
            name: 'Power Quality Issues',
            description: 'Voltage sags, surges, harmonics, or imbalance affecting equipment operation',
            category: 'electrical',
            symptoms: [
              'Random equipment trips',
              'PLC faults or resets',
              'Flickering lights',
              'Overheating transformers',
              'Capacitor failures',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure voltage at equipment',
                expectedResult: 'Voltage within ±10% of nominal',
                tools: ['Digital multimeter'],
                duration: 5,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Check voltage balance between phases',
                expectedResult: 'Phase voltages within 2% of each other',
                tools: ['Digital multimeter'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Install power quality analyzer',
                expectedResult: 'Identify sags, swells, harmonics, or transients',
                tools: ['Power quality analyzer'],
                duration: 60,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Install surge protection',
              'Use UPS for sensitive equipment',
              'Add harmonic filters where needed',
              'Ensure proper grounding',
              'Balance loads across phases',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['motors', 'drives', 'plcs', 'power_supplies'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 2, typical: 8, max: 40, unit: 'hours' },
            costRange: { min: 500, max: 10000, currency: 'USD' },
            industryReferences: ['IEEE 1159', 'IEEE 519'],
          },
        ],
      },
      {
        id: 'process',
        name: 'Process',
        description: 'Root causes related to process parameters and operating conditions',
        icon: 'Activity',
        color: '#10b981',
        rootCauses: [
          {
            id: 'overloading',
            name: 'Equipment Overloading',
            description: 'Operating equipment beyond design capacity causing accelerated wear and failures',
            category: 'process',
            symptoms: [
              'Elevated motor currents',
              'Increased cycle times',
              'Quality issues',
              'Frequent overload trips',
              'Excessive heating',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Compare actual load to equipment rating',
                expectedResult: 'Operating within 80-90% of rated capacity',
                tools: ['Load cell', 'Clamp meter'],
                duration: 15,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Review production records for demand spikes',
                expectedResult: 'Identify peak loading periods',
                tools: ['CMMS/Production system'],
                duration: 20,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Check for process parameter drift',
                expectedResult: 'Parameters within specification',
                tools: ['Process historian'],
                duration: 15,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Implement load monitoring',
              'Establish operating limits',
              'Train operators on capacity limits',
              'Schedule production to avoid overloading',
              'Consider equipment upgrades',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['motors', 'gearboxes', 'bearings', 'structures'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 1, typical: 4, max: 24, unit: 'hours' },
            costRange: { min: 100, max: 5000, currency: 'USD' },
          },
          {
            id: 'contamination',
            name: 'Contamination',
            description: 'Foreign materials affecting equipment operation or product quality',
            category: 'process',
            symptoms: [
              'Quality defects',
              'Clogged filters',
              'Valve malfunctions',
              'Bearing failures',
              'Product contamination',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Inspect filters and strainers',
                expectedResult: 'Identify contaminant type and source',
                tools: ['Flashlight', 'Magnifying glass'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Sample and analyze fluids',
                expectedResult: 'Identify particle count and composition',
                tools: ['Sample bottles', 'Lab analysis'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Trace contamination source',
                expectedResult: 'Identify entry point or generation source',
                tools: ['Process flow diagram'],
                duration: 60,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Implement proper filtration',
              'Control environment (dust, moisture)',
              'Use proper procedures for opening systems',
              'Regular fluid analysis',
              'Maintain positive pressure where applicable',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['filters', 'valves', 'bearings', 'seals', 'pumps'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 2, typical: 8, max: 24, unit: 'hours' },
            costRange: { min: 200, max: 3000, currency: 'USD' },
          },
        ],
      },
      {
        id: 'human',
        name: 'Human Factors',
        description: 'Root causes related to human error, training, or procedures',
        icon: 'User',
        color: '#8b5cf6',
        rootCauses: [
          {
            id: 'operator_error',
            name: 'Operator Error',
            description: 'Incorrect operation leading to equipment damage or process upset',
            category: 'human',
            symptoms: [
              'Unexpected equipment stops',
              'Process excursions',
              'Safety incidents',
              'Quality issues',
              'Damage patterns inconsistent with wear',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review alarm and event logs',
                expectedResult: 'Identify sequence of events leading to failure',
                tools: ['HMI/SCADA system'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Interview operators involved',
                expectedResult: 'Understand actions taken and decisions made',
                tools: ['None'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Review training records',
                expectedResult: 'Verify operator qualification',
                tools: ['Training system'],
                duration: 15,
                skillLevel: 'basic',
              },
            ],
            preventiveActions: [
              'Enhance operator training',
              'Improve procedures',
              'Add interlocks and alarms',
              'Implement error-proofing (poka-yoke)',
              'Conduct regular competency assessments',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['varies'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 1, typical: 4, max: 40, unit: 'hours' },
            costRange: { min: 100, max: 50000, currency: 'USD' },
          },
          {
            id: 'improper_maintenance',
            name: 'Improper Maintenance',
            description: 'Incorrect maintenance procedures or missed maintenance causing failures',
            category: 'human',
            symptoms: [
              'Premature component failures',
              'Recurring issues after maintenance',
              'Missing or incorrect parts',
              'Loose fasteners',
              'Incorrect settings',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review maintenance work order history',
                expectedResult: 'Identify recent maintenance activities',
                tools: ['CMMS'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Verify parts used match specifications',
                expectedResult: 'Correct parts installed',
                tools: ['Parts catalog', 'Inspection'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Check torque values and settings',
                expectedResult: 'All within specification',
                tools: ['Torque wrench', 'Measuring tools'],
                duration: 30,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Use detailed work instructions',
              'Implement quality checks',
              'Require supervisor sign-off',
              'Provide adequate training',
              'Use standardized parts',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['varies'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 2, typical: 6, max: 24, unit: 'hours' },
            costRange: { min: 200, max: 10000, currency: 'USD' },
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'bearing_cascade',
        name: 'Bearing Failure Cascade',
        description: 'Progression from early bearing wear to catastrophic failure',
        sequence: [
          'Lubrication degradation',
          'Increased friction and heat',
          'Surface fatigue and spalling',
          'Vibration increase',
          'Secondary bearing damage',
          'Shaft damage',
          'Motor failure',
        ],
        indicators: [
          'Slight vibration increase at bearing frequencies',
          'Minor temperature rise',
          'Lubricant color change',
        ],
        preventionStrategy: 'Implement vibration-based condition monitoring with alert thresholds for early detection',
      },
      {
        id: 'motor_thermal',
        name: 'Motor Thermal Degradation',
        description: 'Progressive insulation breakdown due to thermal stress',
        sequence: [
          'Overloading or poor cooling',
          'Elevated winding temperatures',
          'Insulation degradation',
          'Turn-to-turn shorts',
          'Phase-to-phase faults',
          'Motor failure',
        ],
        indicators: [
          'Current trending upward',
          'Operating temperature increasing',
          'Insulation resistance decreasing',
        ],
        preventionStrategy: 'Monitor motor current and temperature, perform periodic insulation testing',
      },
    ],
  },

  // =============================================================================
  // LABOR RATES (Manufacturing Industry)
  // =============================================================================
  laborRates: {
    roles: [
      { roleId: 'maintenance_technician', name: 'Maintenance Technician', hourlyRate: 45, currency: 'USD', certifications: ['CMRT', 'Industrial Maintenance'] },
      { roleId: 'instrumentation_technician', name: 'Instrumentation Technician', hourlyRate: 52, currency: 'USD', certifications: ['ISA CCST', 'PLC Programming'] },
      { roleId: 'electrician', name: 'Industrial Electrician', hourlyRate: 55, currency: 'USD', certifications: ['Journeyman License', 'Arc Flash'] },
      { roleId: 'reliability_engineer', name: 'Reliability Engineer', hourlyRate: 85, currency: 'USD', certifications: ['CRE', 'CMRP'] },
      { roleId: 'process_engineer', name: 'Process Engineer', hourlyRate: 80, currency: 'USD', certifications: ['Six Sigma', 'Lean Manufacturing'] },
      { roleId: 'maintenance_planner', name: 'Maintenance Planner', hourlyRate: 55, currency: 'USD', certifications: ['CMMS Certification'] },
      { roleId: 'contractor_mechanic', name: 'Contract Mechanic', hourlyRate: 65, currency: 'USD' },
      { roleId: 'contractor_electrician', name: 'Contract Electrician', hourlyRate: 75, currency: 'USD' },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.5,
  },
}
